import { Injectable, signal, computed } from "@angular/core";
import { User, UserRole, AuthState } from "../models/user.model";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { firstValueFrom, Observable } from "rxjs";
import { MockToggleService } from "./mock-toggle.service";
import { getApiErrorMessage } from "../utils/api-error.util";

export interface SsoLoginUrlResponse {
  valid: boolean;
  user?: User;
  path?: string;
}

const SSO_TOKEN_KEY = "sso_token";
const USER_NAME_KEY = "user_name";
const USER_ROLE_KEY = "user_role";
const VALIDATION_TTL_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({ providedIn: "root" })
export class AuthService {
  private authState = signal<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: false,
  });

  private lastValidationTime = 0;

  readonly isAuthenticated = computed(() => this.authState().isAuthenticated);
  readonly currentUser = computed(() => this.authState().user);
  readonly isEditor = computed(() => this.authState().user?.role === "editor");
  readonly isLoading = computed(() => this.authState().loading);

  constructor(
    private http: HttpClient,
    private router: Router,
    private mockToggle: MockToggleService
  ) {}

  saveToken(token: string): void {
    localStorage.setItem(SSO_TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(SSO_TOKEN_KEY);
  }

  isMockAuthEnabled(): boolean {
    return environment.allowMockToggle && this.mockToggle.isMockEnabled();
  }

  ensureMockSession(): void {
    if (!this.isMockAuthEnabled()) {
      return;
    }

    if (!this.getToken()) {
      this.saveToken("mock-token");
    }

    if (!this.getUserName()) {
      localStorage.setItem(USER_NAME_KEY, "mock.user@purplelab.com");
    }
    this.saveRole("editor");

    this.authState.set({
      isAuthenticated: true,
      loading: false,
      user: {
        id: "mock-user",
        name: "Mock User",
        email: "mock.user@purplelab.com",
        role: "editor",
      },
    });
  }

  // Validates the token against the SSO portal — same behavior as verifySSOToken() in React
  async validateToken(): Promise<boolean> {
    if (this.isMockAuthEnabled()) {
      this.ensureMockSession();
      return true;
    }

    const token = this.getToken();
    if (!token) return false;

    const now = Date.now();
    if (
      this.authState().isAuthenticated &&
      now - this.lastValidationTime < VALIDATION_TTL_MS
    ) {
      return true;
    }

    this.authState.update((s) => ({ ...s, loading: true }));

    try {
      const data = await firstValueFrom(
        this.http.post<{ email?: string; userName?: string }>(
          `${environment.ssoUrl}verifytoken`,
          { token }
        )
      );

      const name = data?.email || data?.userName || "";   

      this.lastValidationTime = Date.now();
      this.authState.update((s) => ({
        ...s,
        isAuthenticated: true,
        loading: false,
        user: {
          id: "temporary-user",
          name: name || "User",
          email: data?.email ?? "",
          role: s.user?.role ?? this.getStoredRole() ?? "viewer",
        },
      }));
      return true;
  
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const status = err?.status;
      const detail = getApiErrorMessage(err);

      console.error("validateToken failed", { status, detail, code: err?.error?.code });

      // 401: invalid or expired token
      if (status === 401) {
        this.authState.set({ isAuthenticated: false, user: null, loading: false });
        return false;
      }

      // 5xx from backend (e.g. /api/login or /permissions/exchange failure)
      if (status >= 500) {
        this.authState.set({ isAuthenticated: false, user: null, loading: false });
        return false;
      }

      // fallback
      this.authState.set({ isAuthenticated: false, user: null, loading: false });
      return false;
    }
  }

  getUserName(): string | null {
    return localStorage.getItem(USER_NAME_KEY);
  }

  logout(): void {
    this.lastValidationTime = 0;

    if (this.isMockAuthEnabled()) {
      localStorage.removeItem(SSO_TOKEN_KEY);
      localStorage.removeItem(USER_NAME_KEY);
      localStorage.removeItem(USER_ROLE_KEY);
      this.authState.set({ isAuthenticated: false, user: null, loading: false });
      this.router.navigate(["/login"]);
      return;
    }

    const token = this.getToken();
    const returnUrl = encodeURIComponent(`${window.location.origin}`);
    localStorage.removeItem(SSO_TOKEN_KEY);
    localStorage.removeItem(USER_NAME_KEY);
    localStorage.removeItem(USER_ROLE_KEY);
    this.authState.set({ isAuthenticated: false, user: null, loading: false });
    window.location.href = `${environment.ssoUrl}logout?returnUrl=${returnUrl}&token=${token}`;
  }

  switchRole(role: UserRole): void {
    this.saveRole(role);
    this.authState.update((s) => ({
      ...s,
      user: s.user ? { ...s.user, role } : null,
    }));
  }

  private saveRole(role: UserRole): void {
    localStorage.setItem(USER_ROLE_KEY, role);
  }

  private getStoredRole(): UserRole | null {
    const role = localStorage.getItem(USER_ROLE_KEY);
    return role === "editor" || role === "viewer" ? role : null;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  login(): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.post<any>(`${environment.apiUrl}/login`, { });
  }
}
